import { useCallback, useMemo } from 'react';
import { OnboardingStep } from '@/types/onboarding';
import { useIsMobile } from '@/hooks/use-is-mobile';

// Importar todos os componentes das etapas de onboarding
import { WelcomeStep } from '@/components/onboarding/steps/WelcomeStep';
import { WhatsAppStep } from '@/components/onboarding/steps/WhatsAppStep';
import { BusinessInfoStep } from '@/components/onboarding/steps/BusinessInfoStep';
import { AIAgentStep } from '@/components/onboarding/steps/AIAgentStep';
import { CalendarStep } from '@/components/onboarding/steps/CalendarStep';
import { ContentStep } from '@/components/onboarding/steps/ContentStep';
import { CompletionStep } from '@/components/onboarding/steps/CompletionStep';

// Importar componentes mobile quando disponíveis
import { WelcomeStepMobile } from '@/components/onboarding/steps/WelcomeStepMobile';
import { CalendarStepMobile } from '@/components/onboarding/steps/CalendarStepMobile';
import { ContentStepMobile } from '@/components/onboarding/steps/ContentStepMobile';

/**
 * Hook para gerenciar as etapas do processo de onboarding
 * Isso centraliza a definição das etapas, permitindo adicionar/remover etapas facilmente
 */
export const useOnboardingSteps = () => {
  // Detectar se estamos em um dispositivo móvel usando o hook especializado
  const isMobile = useIsMobile();
  
  // Definição das etapas do processo de onboarding
  const steps: OnboardingStep[] = useMemo(() => [
    {
      id: 'welcome',
      title: 'Bem-vindo ao Zapcore',
      description: 'Vamos configurar seu assistente virtual em poucos passos',
      component: isMobile ? WelcomeStepMobile : WelcomeStep
    },
    {
      id: 'whatsapp',
      title: 'Conexão WhatsApp',
      description: 'Configure seu número para comunicação com clientes',
      component: WhatsAppStep
    },
    {
      id: 'business_info',
      title: 'Informações da Empresa',
      description: 'Configure os dados do seu negócio',
      component: BusinessInfoStep
    },
    {
      id: 'ai_agent',
      title: 'Assistente IA',
      description: 'Configure seu assistente virtual',
      component: AIAgentStep
    },
    {
      id: 'calendar',
      title: 'Calendário',
      description: 'Configure a integração com seu calendário',
      component: isMobile ? CalendarStepMobile : CalendarStep
    },
    {
      id: 'content',
      title: 'Conteúdo',
      description: 'Adicione documentos e informações ao seu assistente',
      component: isMobile ? ContentStepMobile : ContentStep
    },
    {
      id: 'completion',
      title: 'Conclusão',
      description: 'Parabéns! Seu assistente está pronto para uso',
      component: CompletionStep
    },
  ], [isMobile]);

  // Função para obter uma etapa específica pelo ID
  const getStepById = useCallback((stepId: string): OnboardingStep | undefined => {
    return steps.find(step => step.id === stepId);
  }, [steps]);

  // Função para obter a etapa atual pelo número
  const getStepByNumber = useCallback((stepNumber: number): OnboardingStep | undefined => {
    if (stepNumber < 0 || stepNumber >= steps.length) {
      return undefined;
    }
    return steps[stepNumber];
  }, [steps]);

  // Função para verificar se uma etapa é a última do fluxo
  const isLastStep = useCallback((stepId: string): boolean => {
    const index = steps.findIndex(step => step.id === stepId);
    return index === steps.length - 1;
  }, [steps]);

  // Função para obter o progresso percentual
  const getProgressPercentage = useCallback((currentStep: number): number => {
    if (steps.length <= 1) return 100;
    const progress = Math.max(0, Math.min(100, (currentStep / (steps.length - 1)) * 100));
    return Math.round(progress);
  }, [steps]);

  return {
    steps,
    getStepById,
    getStepByNumber,
    isLastStep,
    getProgressPercentage,
    totalSteps: steps.length,
    isMobile
  };
}; 